/**
 * WMSPage — Phase 2a Warenwirtschaft (Stammdaten).
 *
 * Tabs:
 *  - Artikel: Teile/Reifen/Zubehör + CSV-Import + Filter (low-stock, category)
 *  - Lieferanten: CRUD über Lieferanten-Stammdaten
 *  - Inventuren: GoBD-Inventur-Historie
 *  - VK-Kalkulation: Aufschlags-Regeln
 *
 * Aufruf: /dealer/wms (Header-Link für B2B sichtbar)
 *
 * Jun-2026: In wartbare Sub-Komponenten unter `pages/wms/` aufgeteilt
 * (analog zu `pages/buchhaltung/`).
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Truck, AlertTriangle, Boxes, FileBarChart, Percent, ShoppingCart, Settings, Tags } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { SettingsShortcut } from "../components/dealer/SettingsShortcut";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import { employerAllows } from "../lib/employerModules";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import BarcodeScannerModal from "../components/wms/BarcodeScannerModal";
import InventoryPrecheckModal from "../components/wms/InventoryPrecheckModal";
import DonorPanel from "../components/wms/DonorPanel";
import MarkupRulesPanel from "../components/wms/MarkupRulesPanel";
import ArticleCheckQr from "../components/wms/ArticleCheckQr";
import BarcodeCaptureQr from "../components/wms/BarcodeCaptureQr";
import { fmtEur } from "./wms/wmsUtils";
import ArticleSection from "./wms/ArticleSection";
import SupplierSection from "./wms/SupplierSection";
import ConsignmentSection from "./wms/ConsignmentSection";
import InventorySection from "./wms/InventorySection";
import ReorderSection from "./wms/ReorderSection";
import PurchaseOrdersPage from "./PurchaseOrdersPage";
import PriceLabelsSection from "./wms/PriceLabelsSection";
import SupplierModal from "./wms/SupplierModal";
import ArticleModal from "./wms/ArticleModal";
import ArticleBarcodeModal from "./wms/ArticleBarcodeModal";
import ImportModal from "./wms/ImportModal";
import MovementsModal from "./wms/MovementsModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function WMSPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("articles");
    // Jun-2026 (User-Wunsch): Artikel aus der Liste direkt aufs Etiketten-PDF setzen
    const [labelQueue, setLabelQueue] = useState([]);
    const addToLabelSheet = (a) => {
        setLabelQueue((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
        setTab("labels");
    };
    const [suppliers, setSuppliers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [inventorySessions, setInventorySessions] = useState([]);
    const [markupRules, setMarkupRules] = useState([]);
    const [reorderItems, setReorderItems] = useState([]);
    const [storageLocations, setStorageLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supplierModal, setSupplierModal] = useState(null); // null|{} create / {id...} edit
    const [articleModal, setArticleModal] = useState(null);
    const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scannerMode, setScannerMode] = useState("single");
    const [precheckOpen, setPrecheckOpen] = useState(false);
    const [donorOpen, setDonorOpen] = useState(false);
    const [movementsTarget, setMovementsTarget] = useState(null);
    // filters
    const [searchArt, setSearchArt] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterCat, setFilterCat] = useState("");
    const [filterFam, setFilterFam] = useState("");
    const [onlyLow, setOnlyLow] = useState(false);
    const [ownFilter, setOwnFilter] = useState("all");
    // Server-Pagination (Jun-2026): große Kataloge (50k+) blockweise laden.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    // Mehr-Standort (Jun-2026): Hauptsitz sieht standardmäßig ALLE Standorte
    // (eigener + verknüpfte Filialen) aggregiert; kann auf eine Filiale eingrenzen.
    const [orgBranch, setOrgBranch] = useState("__all__");
    // Jun-2026 (User-Wunsch) — Katalog-Scope: "org" = gesamter (zentraler) Katalog
    // der Organisation, "own" = nur Artikel des eigenen Standorts.
    const [catalogScope, setCatalogScope] = useState("org");

    // Suche entprellen (300 ms) — verhindert eine Abfrage bei jedem Tastendruck.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchArt.trim()), 300);
        return () => clearTimeout(t);
    }, [searchArt]);
    // Bei jeder Filter-/Suchänderung zurück auf Seite 1.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, filterCat, filterFam, onlyLow, ownFilter, orgBranch, catalogScope, pageSize]);

    const loadMeta = useCallback(async () => {
        const branch = orgBranch || "__all__";
        const statParams = { params: { branch, scope: "org" } };
        try {
            const [s, inv, mr] = await Promise.all([
                api.get("/suppliers", statParams),
                api.get("/inventory-sessions"),
                api.get("/wms/markup-rules").catch(() => ({ data: [] })),
            ]);
            setSuppliers(s.data || []);
            setInventorySessions(inv.data || []);
            setMarkupRules(mr.data || []);
            try {
                const [ro, locs] = await Promise.all([
                    api.get("/wms/reorder-list"),
                    api.get("/dealer/storage-locations").catch(() => ({ data: [] })),
                ]);
                setReorderItems(ro.data?.items || []);
                setStorageLocations(locs.data || []);
            } catch (e) { /* best effort */ }
        } catch (e) { /* Meta best effort */ }
    }, [orgBranch]);

    const loadArticles = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterCat) params.set("category", filterCat);
            if (filterFam) params.set("family_group_label", filterFam);
            if (debouncedSearch) params.set("search", debouncedSearch);
            if (onlyLow) params.set("low_stock", "true");
            if (ownFilter !== "all") params.set("ownership", ownFilter);
            params.set("limit", String(pageSize));
            params.set("offset", String((page - 1) * pageSize));
            const branch = orgBranch || "__all__";
            params.set("branch", branch);
            if (catalogScope === "own") params.set("scope", "own");
            const a = await api.get(`/articles?${params.toString()}`);
            setArticles(a.data || []);
            const tc = a.headers?.["x-total-count"];
            setTotal(tc != null ? (parseInt(tc, 10) || 0) : (a.data || []).length);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        }
        setLoading(false);
    }, [filterCat, filterFam, debouncedSearch, onlyLow, ownFilter, orgBranch, catalogScope, page, pageSize]);

    const loadAll = useCallback(() => { loadMeta(); loadArticles(); }, [loadMeta, loadArticles]);

    useEffect(() => {
        if (user === false) navigate("/login");
        else if (user && user.role !== "b2b" && user.role !== "admin" && !employerAllows(user, "wms")) navigate("/");
        if (user) loadMeta();
    }, [user, navigate, loadMeta]);

    useEffect(() => {
        if (user) loadArticles();
    }, [user, loadArticles]);

    // Jun-2026 (User-Wunsch) — Deep-Links aus dem Header-Untermenü:
    // ?action=new-article öffnet die Artikelanlage, ?action=scan-barcode den Scanner.
    useEffect(() => {
        if (!user) return;
        const sp = new URLSearchParams(window.location.search);
        const action = sp.get("action");
        if (action === "new-article") setArticleModal({});
        else if (action === "scan-barcode") setScannerOpen(true);
        // Jul-2026 (User-Wunsch): Deep-Links aus dem Bestell-Hinweis-Banner
        if (sp.get("tab") === "reorder") setTab("reorder");
        if (sp.get("low")) { setTab("articles"); setOnlyLow(true); }
        // Aug-2026 (User-Wunsch): ?article=<id>&q=<sku> öffnet den Artikel direkt
        // (Deep-Link z. B. aus der EAN-Nachpflege).
        const artId = sp.get("article");
        const artQ = sp.get("q");
        if (artId || artQ) {
            (async () => {
                try {
                    const r = await api.get(`/articles?search=${encodeURIComponent(artQ || "")}&limit=10&branch=__all__`);
                    const hit = (r.data || []).find((x) => x.id === artId) || (r.data || [])[0];
                    if (hit) setArticleModal(hit);
                    else { setSearchArt(artQ || ""); toast.info("Artikel nicht gefunden — Suche gesetzt"); }
                } catch {
                    setSearchArt(artQ || "");
                }
            })();
        }
        if (action || sp.get("tab") || sp.get("low") || artId || artQ) navigate("/dealer/wms", { replace: true });
    }, [user, navigate]);

    // window.confirm wird in der eingebetteten Vorschau blockiert → eigener Dialog
    const [confirmDel, setConfirmDel] = useState(null); // {type:"article"|"supplier", id}

    const deleteSupplier = (id) => setConfirmDel({ type: "supplier", id });
    const deleteArticle = (id) => setConfirmDel({ type: "article", id });

    const execDelete = async () => {
        if (!confirmDel) return;
        const { type, id } = confirmDel;
        setConfirmDel(null);
        try {
            await api.delete(type === "supplier" ? `/suppliers/${id}` : `/articles/${id}`);
            toast.success(type === "supplier" ? "Lieferant entfernt/deaktiviert" : "Artikel entfernt/deaktiviert");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    if (!user) return <div className="p-8">Lade…</div>;

    const supById = Object.fromEntries(suppliers.map((s) => [s.id, s]));

    return (
        <div className="min-h-screen bg-gray-50" data-testid="wms-page">
            <EmployeeContextBanner />
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight inline-flex items-center gap-2">Warenwirtschaft <SettingsShortcut to="/dashboard?tab=settings#settings-warenwirtschaft" title="Warenwirtschaft-Einstellungen" testid="settings-shortcut-warenwirtschaft" dark /></h1>
                            {/* Aug-2026 (User-Wunsch): Beschreibungstext entfernt. */}
                        </div>
                        {/* Mehr-Standort: Hauptsitz sieht alle verknüpften Standorte; kann eingrenzen. */}
                        <div className="flex flex-wrap items-center gap-2">
                            <OrgBranchSwitcher user={user} value={orgBranch === "__all__" ? "__all__" : orgBranch}
                                onChange={(b) => setOrgBranch(b || "self")} dark allowAll />
                        </div>
                    </div>

                    {/* KPI-Kacheln entfernt (Jul-2026, User-Wunsch) — Kennzahlen
                        gehören in die Auswertung; hier nur Arbeits-Werkzeuge. */}

                    {/* Zeile 1+2 links · „Artikel checken"-QR rechtsbündig daneben (Jul-2026, User-Wunsch) */}
                    <div className="mt-3 flex gap-4 items-end">
                        <div className="flex-1 min-w-0">
                            {/* Zeile 1: Navigation (Routing) — Dashboard ganz links, Rest rechtsbündig */}
                            <div className="flex flex-wrap gap-2 items-center" data-testid="wms-nav-row">
                        <Link to="/dashboard" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm font-bold" data-testid="back-to-dashboard">
                            ← Dashboard
                        </Link>
                        <div className="flex-1" />
                        <Link to="/dealer/invoices" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-invoices">
                            → Zu Rechnungen
                        </Link>
                        <button type="button"
                            onClick={async () => {
                                if (!window.confirm("Fehlbuchungen korrigieren?\n\nScan-Buchungen, die auf einem Artikel eines ANDEREN Standorts gelandet sind, werden automatisch an den Scan-Standort umgebucht.")) return;
                                try {
                                    const { data } = await api.post("/articles/fix-misbooked-scans");
                                    toast.success(`${data.fixed} Fehlbuchung(en) korrigiert (${data.checked} Scans geprüft)`);
                                    if (data.fixed > 0) loadAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Korrektur fehlgeschlagen"); }
                            }}
                            className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm"
                            title="Scan-Buchungen auf fremde Standort-Artikel automatisch an den Scan-Standort umbuchen"
                            data-testid="fix-misbooked-btn">
                            ⚡ Fehlbuchungen korrigieren
                        </button>
                        <button type="button"
                            onClick={async () => {
                                if (!window.confirm("VK neu kalkulieren?\n\nAlle Artikel, deren VK noch dem Hersteller-UVP entspricht (aus alten Scans), werden über Ihre Aufschlagsregeln neu berechnet. Greift keine EK-Regel, gilt der Fallback: VK = Hersteller-UVP × 1,3 (wie Piaggio-Preisliste). Der UVP wandert dabei ins UVP-Feld.")) return;
                                try {
                                    const { data } = await api.post("/articles/recalc-vk-from-uvp");
                                    toast.success(`${data.updated} Artikel neu kalkuliert (${data.checked} geprüft, ${data.skipped_no_rule} ohne passende Regel/EK übersprungen)`);
                                    if (data.updated > 0) loadAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Neukalkulation fehlgeschlagen"); }
                            }}
                            className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm"
                            title="Alt-Artikel, deren VK noch der Hersteller-UVP ist, einmalig mit Ihrem Aufschlag neu berechnen"
                            data-testid="recalc-vk-btn">
                            ⚡ VK neu kalkulieren
                        </button>
                        <Link to="/dealer/wms/scan-delivery" className="text-xs uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:bg-[#E6B800] font-bold border border-[#FFCC00] px-3 py-2 rounded-sm" data-testid="link-scan-delivery">
                            ⚡ Lieferschein scannen (KI)
                        </Link>
                        <Link to="/dealer/ai-enrich" className="text-xs uppercase tracking-wider text-white bg-purple-600 hover:bg-purple-700 font-bold border border-purple-600 px-3 py-2 rounded-sm" data-testid="link-ai-enrich">
                            ⚡ KI-Artikel-Anreicherung
                        </Link>
                    </div>
                    {/* Zeile 2: Funktionen dieser Seite — rechtsbündig */}
                    <div className="mt-2 flex flex-wrap gap-2 justify-end" data-testid="wms-quick-actions">
                        <button type="button" onClick={() => setTab("reorder")} className="text-xs uppercase tracking-wider text-amber-300 hover:text-white font-bold border border-amber-500 px-3 py-2 rounded-sm" data-testid="wms-open-reorder-list">
                            ⚠ Offene Bestellliste / Rückstand
                        </button>
                        <button type="button" onClick={() => { setTab("articles"); setOnlyLow(true); }} className="text-xs uppercase tracking-wider text-amber-300 hover:text-white font-bold border border-amber-500 px-3 py-2 rounded-sm" data-testid="wms-open-lowstock">
                            ⚠ Mindestbestandsliste
                        </button>
                        <button type="button" onClick={() => { setScannerMode("single"); setScannerOpen(true); }} className="text-xs uppercase tracking-wider text-[#FFCC00] hover:bg-[#FFCC00]/10 font-bold border border-[#FFCC00] px-3 py-2 rounded-sm" data-testid="art-scan-btn">
                            Barcode scannen
                        </button>
                        <button type="button" onClick={() => setBarcodeModalOpen(true)} className="text-xs uppercase tracking-wider text-[#FFCC00] hover:bg-[#FFCC00]/10 font-bold border border-[#FFCC00] px-3 py-2 rounded-sm" title="Fehlende Barcodes an Artikeln nacherfassen (Handy-Scan oder manuell) + Etiketten drucken" data-testid="art-barcode-btn">
                            Barcodes nacherfassen
                        </button>
                        <button type="button" onClick={() => setArticleModal({})} className="text-xs uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:bg-[#E6B800] font-bold border border-[#FFCC00] px-3 py-2 rounded-sm" data-testid="art-create-btn">
                            + Neuer Artikel
                        </button>
                    </div>
                        </div>
                        {/* Jul-2026 (User-Wunsch) — QR „Artikel checken" rechtsbündig neben den Buttons:
                            Handy-Scan zeigt Artikeldaten + Bestand ALLER Standorte (standortübergreifend). */}
                        <ArticleCheckQr />
                        {/* Jul-2026 (User-Wunsch) — QR „Barcode nacherfassen": Artikelnummer am
                            Handy eintippen → Scan-Code mit der Kamera erfassen & speichern. */}
                        <BarcodeCaptureQr />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                {/* Aug-2026 (User-Wunsch): alle Tabs in EINER Zeile */}
                <div className="flex gap-1 border-b border-gray-200 flex-nowrap overflow-x-auto">
                    {[
                        { k: "articles", l: "Artikel", count: total, icon: Package },
                        // Aug-2026 (User-Wunsch): „Bestellungen" (Lieferanten-Bestellwesen)
                        // als Tab direkt rechts von Artikel statt eigener Seite/Button.
                        { k: "orders", l: "Bestellung", icon: Truck },
                        { k: "reorder", l: "Bestellen / Rückstand", count: reorderItems.filter((i) => (i.status || "open") === "open").length, icon: ShoppingCart },
                        { k: "inventories", l: "Inventur", count: inventorySessions.length, icon: FileBarChart },
                        { k: "labels", l: "Preisschild", icon: Tags },
                        { k: "markup", l: "Kalkulation", icon: Percent },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`px-2.5 py-3 text-[13px] font-bold uppercase tracking-wide border-b-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === t.k ? "border-[#111827] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                            data-testid={`tab-${t.k}`}
                        >
                            <t.icon className="w-4 h-4" /> {t.l}
                            {t.count !== undefined && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-sm text-[10px]">{t.count}</span>}
                        </button>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Aug-2026 (User-Report): aktiver Mindestbestands-Filter DEUTLICH anzeigen,
                    damit klar ist, warum nur wenige Artikel sichtbar sind. */}
                {tab === "articles" && onlyLow && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-sm border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 font-bold" data-testid="wms-lowstock-active-banner">
                        ⚠ Mindestbestands-Filter aktiv — es werden NUR Artikel unter Mindestbestand angezeigt ({total.toLocaleString("de-DE")} Treffer).
                        <button type="button" onClick={() => setOnlyLow(false)}
                            className="underline text-amber-900 hover:text-amber-700 font-black uppercase tracking-wider"
                            data-testid="wms-lowstock-clear">
                            Filter aufheben — alle Artikel anzeigen
                        </button>
                    </div>
                )}
                {tab === "articles" ? (
                    <ArticleSection
                        articles={articles}
                        supById={supById}
                        suppliers={suppliers}
                        markupRules={markupRules}
                        loading={loading}
                        searchArt={searchArt} setSearchArt={setSearchArt}
                        filterCat={filterCat} setFilterCat={setFilterCat}
                        filterFam={filterFam} setFilterFam={setFilterFam}
                        onlyLow={onlyLow} setOnlyLow={setOnlyLow}
                        ownFilter={ownFilter} setOwnFilter={setOwnFilter}
                        catalogScope={catalogScope} setCatalogScope={setCatalogScope}
                        showScope={user?.branch_role === "filiale" || (user?.branch_role === "hauptsitz" && orgBranch !== "__all__")}
                        page={page} setPage={setPage}
                        pageSize={pageSize} setPageSize={setPageSize}
                        total={total}
                        onCreate={() => setArticleModal({})}
                        onEdit={(a) => setArticleModal(a)}
                        onDelete={deleteArticle}
                        onMovements={(a) => setMovementsTarget(a)}
                        onScan={() => setScannerOpen(true)}
                        onDonors={() => setDonorOpen(true)}
                        onAddLabel={addToLabelSheet}
                    />
                ) : tab === "orders" ? (
                    <PurchaseOrdersPage embedded />
                ) : loading ? (
                    <div className="text-center py-12 text-gray-500">Lade…</div>
                ) : tab === "suppliers" ? (
                    <SupplierSection
                        suppliers={suppliers}
                        onCreate={() => setSupplierModal({})}
                        onEdit={(s) => setSupplierModal(s)}
                        onDelete={deleteSupplier}
                    />
                ) : tab === "labels" ? (
                    <PriceLabelsSection pendingArticles={labelQueue} onPendingConsumed={() => setLabelQueue([])} />
                ) : tab === "markup" ? (
                    /* Nov-2026 (User-Wunsch): Abschläge/Kalkulation direkt hier pflegen —
                       editierbar für Hauptsitz/Filial-Konten und Filialleitung. */
                    <MarkupRulesPanel readOnly={!(user?.role === "b2b" || ["filialleiter", "verkaufsleiter", "geschaeftsfuehrer", "inhaber"].includes(user?.employee_function))} />
                ) : tab === "reorder" ? (
                    <ReorderSection
                        items={reorderItems}
                        suppliers={suppliers}
                        storageLocations={storageLocations}
                        onReload={loadMeta}
                        onEditArticle={(a) => setArticleModal(a)}
                    />
                ) : tab === "consignment" ? (
                    <ConsignmentSection />
                ) : (
                    <InventorySection
                        sessions={inventorySessions}
                        onScan={() => setPrecheckOpen(true)}
                    />
                )}
            </div>

            {supplierModal && (
                <SupplierModal
                    supplier={supplierModal}
                    onClose={() => setSupplierModal(null)}
                    onSaved={() => { setSupplierModal(null); loadAll(); }}
                />
            )}
            {barcodeModalOpen && <ArticleBarcodeModal open onClose={() => setBarcodeModalOpen(false)} />}
            {articleModal && (
                <ArticleModal
                    article={articleModal}
                    suppliers={suppliers}
                    articles={articles}
                    onClose={() => setArticleModal(null)}
                    onSaved={() => { setArticleModal(null); loadAll(); }}
                />
            )}
            {importOpen && (
                <ImportModal
                    onClose={() => setImportOpen(false)}
                    onDone={() => { setImportOpen(false); loadAll(); }}
                />
            )}
            {movementsTarget && (
                <MovementsModal
                    article={movementsTarget}
                    suppliers={suppliers}
                    onClose={() => setMovementsTarget(null)}
                    onBooked={() => { loadAll(); }}
                />
            )}
            {scannerOpen && (
                <BarcodeScannerModal
                    initialMode={scannerMode}
                    onClose={() => { setScannerOpen(false); setScannerMode("single"); }}
                    onBooked={() => loadAll()}
                    onCreateUnknown={(ean) => {
                        setScannerOpen(false);
                        setArticleModal({ ean, sku: "", name: "" });
                    }}
                />
            )}
            {precheckOpen && (
                <InventoryPrecheckModal
                    onClose={() => setPrecheckOpen(false)}
                    onStart={() => { setScannerMode("session"); setScannerOpen(true); }}
                />
            )}
            <DonorPanel
                open={donorOpen}
                onClose={() => setDonorOpen(false)}
                onCreateArticle={(donorId, label) => {
                    setDonorOpen(false);
                    setArticleModal({ source_donor_id: donorId, _donorLabel: label });
                }}
            />
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
                <AlertDialogContent data-testid="wms-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDel?.type === "supplier" ? "Lieferant löschen?" : "Artikel löschen?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.type === "supplier"
                                ? "Bei vorhandenen Artikeln wird der Lieferant nur deaktiviert."
                                : "Mit vorhandener Bewegungs-Historie wird der Artikel nur deaktiviert, sonst endgültig gelöscht."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="wms-delete-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={execDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="wms-delete-confirm">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function KPI({ label, value, icon: Icon, warn }) {
    return (
        <div className={`border rounded-sm p-3 ${warn ? "bg-red-500/10 border-red-500/50" : "bg-white/5 border-white/10"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1 flex items-center gap-1">
                {Icon && <Icon className="w-3 h-3" />}
                {label}
            </div>
            <div className={`font-display font-black text-2xl ${warn ? "text-red-300" : "text-white"}`}>{value}</div>
        </div>
    );
}
